'use client';

/**
 * API namespace `academy` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L3611-L3759).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  AcademyAdminCourseDetail, AcademyAdminModule,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const academy = {
  /**
   * GET /api/admin/academy/courses — admin list (incl. draft + archived).
   * Fase 0: this is the ONLY endpoint that returns the 8 placeholder
   * draft courses for the admin preview at /admin/academy.
   *
   * Spec ref: SPEC-071 §4.10.
   */
  async listCourses(signal?: AbortSignal): Promise<AcademyAdminCourse[]> {
    const res = await apiFetch<{ data: AcademyAdminCourse[] } | AcademyAdminCourse[]>(
      '/admin/academy/courses',
      { signal },
    );
    // The backend handler returns `{ data: courses }` (array, NOT
    // wrapped in { items, total } — admin academy list is small per
    // Fase 0, no pagination needed). The apiFetch wrapper unwraps the
    // outer `{ data: ... }` envelope, so we receive the array directly.
    // Defensive: if the array is wrapped again (e.g. future migration
    // to Paginated shape), unwrap one more level.
    if (Array.isArray(res)) return res;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const maybeArr = (res as any)?.data;
    return Array.isArray(maybeArr) ? maybeArr : [];
  },
  /**
   * POST /api/admin/academy/courses — create course (draft status).
   *
   * Spec ref: SPEC-071 §4.10.
   */
  async createCourse(body: {
    track: AcademyTrack;
    category: string;
    slug: string;
    title: string;
    description: string;
    difficulty?: AcademyCourseDifficulty;
    estimatedHours?: number;
    tags?: string[];
  }): Promise<AcademyAdminCourse> {
    return apiFetch<AcademyAdminCourse>('/admin/academy/courses', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  /**
   * PUT /api/admin/academy/courses/:id — update course fields.
   *
   * Spec ref: SPEC-071 §4.10.
   */
  async updateCourse(
    id: string,
    body: Partial<{
      title: string;
      description: string;
      difficulty: AcademyCourseDifficulty;
      estimatedHours: number;
      tags: string[];
      category: string;
    }>,
  ): Promise<AcademyAdminCourse> {
    return apiFetch<AcademyAdminCourse>(`/admin/academy/courses/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  /**
   * PATCH /api/admin/academy/courses/:id/publish — status: draft → published.
   *
   * Spec ref: SPEC-071 §4.10.
   */
  async publishCourse(id: string): Promise<AcademyAdminCourse> {
    return apiFetch<AcademyAdminCourse>(
      `/admin/academy/courses/${encodeURIComponent(id)}/publish`,
      { method: 'PATCH' },
    );
  },
  /**
   * PATCH /api/admin/academy/courses/:id/archive — status: published → archived.
   *
   * Spec ref: SPEC-071 §4.10.
   */
  async archiveCourse(id: string): Promise<AcademyAdminCourse> {
    return apiFetch<AcademyAdminCourse>(
      `/admin/academy/courses/${encodeURIComponent(id)}/archive`,
      { method: 'PATCH' },
    );
  },
  /**
   * POST /api/admin/academy/courses/:id/modules — create module.
   *
   * V140-ADMIN-ACADEMY-EDIT: body now includes `videoUrl` (mirrors the
   * instructor-side pattern — Bunny Stream HLS OR external YouTube/Vimeo/
   * Loom URL). `null` clears the URL; `undefined` omits it.
   *
   * Spec ref: SPEC-071 §4.10.
   */
  async createModule(
    courseId: string,
    body: {
      order: number;
      title: string;
      description: string;
      content?: string;
      videoUrl?: string | null;
      estimatedMinutes?: number;
    },
  ): Promise<AcademyAdminModule> {
    return apiFetch(
      `/admin/academy/courses/${encodeURIComponent(courseId)}/modules`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },
  /**
   * PUT /api/admin/academy/courses/:id/modules/:mid — update module.
   *
   * V140-ADMIN-ACADEMY-EDIT (Founder directive 2026-11-02): new method —
   * the admin edit dialog calls this to update an existing module's fields
   * (title, description, content, videoUrl, estimatedMinutes). All fields
   * are `Partial<>` — only the fields present in the body are updated
   * (backend allowlist drops unknown fields, AC-P1-2).
   *
   * Spec ref: SPEC-071 §4.10.
   */
  async updateModule(
    courseId: string,
    moduleId: string,
    body: Partial<{
      title: string;
      description: string;
      content: string;
      videoUrl: string | null;
      estimatedMinutes: number;
    }>,
  ): Promise<AcademyAdminModule> {
    return apiFetch<AcademyAdminModule>(
      `/admin/academy/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(moduleId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },
  /**
   * GET /api/admin/academy/courses/:id — admin get course WITH modules.
   *
   * V140-ADMIN-ACADEMY-EDIT (Founder directive 2026-11-02): new method —
   * fetches a single course + its modules (sorted by order) for the admin
   * edit dialog at /admin/academy. Returns AcademyAdminCourseDetail
   * (course fields + `modules: AcademyAdminModule[]`).
   *
   * Spec ref: SPEC-071 §4.10.
   */
  async getCourseDetail(id: string, signal?: AbortSignal): Promise<AcademyAdminCourseDetail> {
    return apiFetch<AcademyAdminCourseDetail>(
      `/admin/academy/courses/${encodeURIComponent(id)}`,
      { signal },
    );
  },
  /**
   * PATCH /api/admin/academy/certificates/:id/void — void certificate
   * (superadmin only, irreversible). INV-ACAD-03.
   *
   * Spec ref: SPEC-071 §4.10.
   */
  async voidCertificate(
    certificateId: string,
    reason: string,
  ): Promise<{ status: 'voided'; voidedAt: string; voidedReason: string }> {
    return apiFetch(
      `/admin/academy/certificates/${encodeURIComponent(certificateId)}/void`,
      {
        method: 'PATCH',
        body: JSON.stringify({ reason }),
      },
    );
  },
};
